import { ClassifierCoverageRow, Level } from '../types'
import { getClassifierById } from '../mockData'
import ModalityPill from '../../../components/badges/ModalityPill'

interface Props {
    level: Level
    rows: ClassifierCoverageRow[]
}

// Classifiers are applied to profiles only. On a profile page each row is simply
// "applied here"; on team/org pages each row aggregates the profiles beneath, and
// the Coverage column surfaces gaps (profiles added after a bulk-add don't pick
// classifiers up automatically — there is no inheritance).
export default function ClassifierConfigTable({ level, rows }: Props) {
    const isProfileLevel = level === 'profile'
    const showCoverageCol = !isProfileLevel
    const scopeNoun = level === 'org' ? 'organisation' : level

    return (
        <div>
            {/* One line of model-setting copy replaces the old cascade banner. */}
            {!isProfileLevel && rows.length > 0 && (
                <div className="border-b border-gray-100 bg-gray-50 px-3 py-2 text-xs text-gray-800">
                    Applied per profile. Adding a classifier here applies it to all{' '}
                    {rows[0].totalProfiles} profiles in this {scopeNoun} now — profiles added
                    later don't pick it up automatically.
                </div>
            )}

            <table className="w-full border-collapse text-sm">
                <thead>
                    <tr>
                        <th scope="col" className="border-b border-gray-100 py-2 pl-3 pr-2 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-800">
                            Classifier
                        </th>
                        <th scope="col" className="border-b border-gray-100 px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-800">
                            Modality
                        </th>
                        {showCoverageCol && (
                            <th scope="col" className="border-b border-gray-100 px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-800">
                                Coverage
                            </th>
                        )}
                        <th scope="col" className="border-b border-gray-100 px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-800">
                            Version
                        </th>
                        <th scope="col" className="border-b border-gray-100 px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-800">
                            <span className="sr-only">Actions</span>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => {
                        const classifier = getClassifierById(row.classifierId)
                        const title = classifier?.title ?? row.classifierId
                        const description = classifier?.description ?? ''
                        const modality = classifier?.modality ?? 'text'
                        const missing = row.totalProfiles - row.appliedProfiles
                        const hasGap = showCoverageCol && missing > 0

                        return (
                            <tr key={row.classifierId}>
                                {/* Classifier name + description */}
                                <td className="border-b border-gray-50 py-2.5 pl-3 pr-2 align-middle">
                                    <div className="font-semibold text-gray-900">{title}</div>
                                    <div className="text-[11.5px] text-gray-800">{description}</div>
                                </td>

                                {/* Modality */}
                                <td className="border-b border-gray-50 px-2 py-2.5 align-middle">
                                    <ModalityPill modality={modality} />
                                </td>

                                {/* Coverage — team/org only. A gap means some profiles
                                    beneath don't have the classifier (e.g. added later). */}
                                {showCoverageCol && (
                                    <td className="border-b border-gray-50 px-2 py-2.5 align-middle text-xs">
                                        <span
                                            className={`font-mono tabular-nums ${
                                                hasGap ? 'font-semibold text-suspect' : 'text-gray-800'
                                            }`}
                                        >
                                            {row.appliedProfiles}/{row.totalProfiles}
                                        </span>{' '}
                                        <span className="text-gray-800">profiles</span>
                                        {hasGap && (
                                            <button
                                                type="button"
                                                className="mt-0.5 block text-xs font-semibold text-arwen-700 hover:underline"
                                            >
                                                Apply to remaining {missing}
                                            </button>
                                        )}
                                    </td>
                                )}

                                {/* Live version. The threshold is deliberately absent —
                                    it's internal to the classifier definition and only
                                    shown on the edit page. */}
                                <td className="border-b border-gray-50 px-2 py-2.5 align-middle text-xs text-gray-800">
                                    <span className="font-mono tabular-nums">v{row.liveVersion}</span>
                                </td>

                                {/* Actions — remove means remove; there is no disable */}
                                <td className="border-b border-gray-50 px-2 py-2.5 align-middle">
                                    {isProfileLevel ? (
                                        <button type="button" className="text-xs font-semibold text-severe">
                                            Remove
                                        </button>
                                    ) : (
                                        // Bulk remove from every profile beneath — the real
                                        // flow confirms with a blast radius first.
                                        <button type="button" className="whitespace-nowrap text-xs font-semibold text-severe">
                                            Remove from all profiles
                                        </button>
                                    )}
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    )
}
